import { onError } from '@apollo/client/link/error'
import { NetworkError as NotificationError } from '@/utils/notification'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { debounce } from '@/utils/tools'
import enums from '@/utils/enum'

const errorQueue: string[] = [] // Queue to store error messages

// Function to generate a combined error message from the queue
const generateCombinedErrorMessage = (messages: string[]) => {
  if (messages.length === 0) return $t('An unknown error occurred.')

  return messages.pop()
}

// Debounced notification function
const debouncedNotification = debounce(() => {
  if (errorQueue.length > 0) {
    const combinedMessage = generateCombinedErrorMessage(errorQueue)
    errorQueue.length = 0 // Clear the queue
    new NotificationError({
      msg: combinedMessage,
      severity: enums.notification.severity.CRITICAL,
    }).dispatch()
  }
}, 300)

// Log any GraphQL errors or network errors that occurred
export const ErrorHandlingLink = onError(({ graphQLErrors, networkError }) => {
  // Add GraphQL errors to the queue
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      errorQueue.push($t('An unexpected error occurred while processing your request.'))
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
    })
  }

  // Add network error to the queue
  if (networkError) {
    const networkMessage =
      networkError.name === 'ServerError'
        ? $t('A server error occurred. Please try again later.')
        : networkError.name === 'NetworkError'
          ? $t('A network error occurred. Please check your connection.')
          : $t('An unexpected network error occurred.')
    errorQueue.push(networkMessage)
    console.error(`[Network error]: ${networkError}`)
  }

  // Trigger debounced notification
  debouncedNotification()
})
